import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CalendarPlus, Trash2, CalendarDays } from "lucide-react";

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const reload = () => api.get("/deadlines").then((r) => setItems(r.data));
  useEffect(() => {
    reload();
    api.get("/properties").then((r) => setProps(r.data));
  }, []);

  const del = async (id) => {
    await api.delete(`/deadlines/${id}`);
    toast.success("Deadline removed");
    reload();
  };

  const today = new Date();
  const grouped = items.reduce((acc, d) => {
    const dt = new Date(d.date);
    const days = Math.round((dt - today) / (1000 * 60 * 60 * 24));
    const bucket = days < 0 ? "Overdue" : days <= 7 ? "This week" : days <= 30 ? "This month" : days <= 90 ? "Next 90 days" : "Later";
    (acc[bucket] = acc[bucket] || []).push({ ...d, days });
    return acc;
  }, {});
  const order = ["Overdue", "This week", "This month", "Next 90 days", "Later"];

  return (
    <div className="space-y-6 animate-slide-up" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Deadlines</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display">Renewal calendar</h1>
        </div>
        <button onClick={() => setShowAdd(true)} disabled={props.length === 0} data-testid="add-deadline" className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-slate-800 disabled:opacity-40">
          <CalendarPlus className="w-4 h-4" /> Add deadline
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <CalendarDays className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <div className="text-slate-500 text-sm">No deadlines scheduled. Add renewals from property checklists or manually.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {order.map((k) => grouped[k] && (
            <div key={k}>
              <h2 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">{k}</h2>
              <div className="space-y-2">
                {grouped[k].map((d) => (
                  <div key={d.id} data-testid={`cal-item-${d.id}`}
                    className={`bg-white border rounded-xl p-4 flex items-center justify-between gap-4 ${d.days < 0 ? "border-rose-200" : d.days <= 7 ? "border-amber-200" : "border-slate-200"}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-14 h-14 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${d.days < 0 ? "bg-rose-50 text-rose-700" : d.days <= 7 ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
                        <div className="text-[10px] font-mono uppercase">{new Date(d.date).toLocaleString("en-US", { month: "short" })}</div>
                        <div className="text-xl font-bold font-display leading-none">{new Date(d.date).getDate()}</div>
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{d.title}</div>
                        <div className="text-xs text-slate-500 truncate">{d.property_name}{d.rule_code ? ` · ${d.rule_code}` : ""}</div>
                        <div className="text-[11px] font-mono mt-0.5 text-slate-600">{d.days === 0 ? "Today" : d.days < 0 ? `${Math.abs(d.days)}d overdue` : `in ${d.days} days`}</div>
                      </div>
                    </div>
                    <button onClick={() => del(d.id)} data-testid={`del-cal-${d.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddDeadlineModal props={props} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}
    </div>
  );
}

function AddDeadlineModal({ props, onClose, onSaved }) {
  const [f, setF] = useState({ property_id: props[0]?.id || "", title: "", date: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/deadlines", f);
      toast.success("Deadline added");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose} data-testid="add-deadline-modal">
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100">
          <h2 className="font-display text-xl font-semibold">Add deadline</h2>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-600">Property</span>
            <select value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })} data-testid="dl-property"
              className="mt-1.5 w-full px-4 py-3 rounded-lg border border-slate-300 bg-white outline-none">
              {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-600">Title</span>
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="dl-title" placeholder="e.g. STR permit renewal"
              className="mt-1.5 w-full px-4 py-3 rounded-lg border border-slate-300 outline-none focus:border-sky-500" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-600">Date</span>
            <input required type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dl-date"
              className="mt-1.5 w-full px-4 py-3 rounded-lg border border-slate-300 outline-none focus:border-sky-500" />
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50">Cancel</button>
            <button disabled={saving} data-testid="submit-deadline" className="flex-1 bg-slate-900 text-white py-2.5 rounded-lg hover:bg-slate-800 disabled:opacity-50">
              {saving ? "Saving…" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
